/*!
 * BOPF nav injection: The Pop Up   [v7 - 2026-04-26]
 *
 * Adds a "The Pop Up" link to the storefront nav, with a hand-drawn
 * terracotta wavy-underline scribble matching the Hermes page editorial
 * style. Inserts after the Shop item.
 *
 * Targets nav#NavStandard (desktop) and ul.mobile-nav (mobile drawer)
 * via structural auto-discovery rather than fixed CSS selectors.
 *
 * Install:
 *   1. Save this file in your theme as: assets/bopf-nav-popup.js
 *   2. In layout/theme.liquid, just before </body>:
 *        <script src="{{ 'bopf-nav-popup.js' | asset_url }}?v=4" defer></script>
 *      The ?v=4 query param forces Shopify's CDN to skip its cache.
 *      Bump it (?v=5, ?v=6...) any time you push a new version of this file.
 *
 * Tuning:
 *   - To change position, edit ANCHOR_PATTERNS below.
 *     Examples:
 *       insert after Hermes:   add /^herm[eè]s$/i
 *       insert at very start:  set ANCHOR_PATTERNS = []
 *       insert at very end:    set INSERT_AT_END = true
 */

console.log('[BOPF nav v7] script parsed, readyState =', document.readyState);

(function () {
  'use strict';

  var POP_UP_HREF    = '/pages/the-bopf-popups-alquoz';
  var POP_UP_LABEL   = 'The Pop Up';
  var INJECTED_FLAG  = 'data-bopf-pop-injected';
  var SCRIBBLE_COLOR = '#A0563E';
  var INSERT_AT_END  = false;

  // Originally we used an SVG underline path positioned absolute below
  // the link. That gets clipped by overflow:hidden on parent containers
  // in some themes (BOPF mobile drawer being one). text-decoration:wavy
  // is the reliable equivalent: rendered by the browser as part of the
  // text itself, so nothing can clip it.

  // No more guessing class names. We auto-discover any element in the
  // header that LOOKS like a nav menu - meaning it has multiple direct
  // children, most of which contain a link. This catches every theme
  // structure: <ul role="list">, <nav> with direct <a>, custom elements,
  // etc. Theme-specific selectors are unnecessary.

  // Try to insert AFTER the first item that matches one of these. If none
  // match, fall back to the top of the menu (or end if INSERT_AT_END is set).
  var ANCHOR_PATTERNS = [
    /^shop$/i,
    /^shop\s+all/i,
    /^catalog$/i,
    /^all\s+products$/i,
    /^store$/i,
    /^browse$/i,
    /^collections$/i
  ];

  function injectStyles() {
    if (document.getElementById('bopf-nav-popup-styles')) return;
    var style = document.createElement('style');
    style.id = 'bopf-nav-popup-styles';
    // Aggressive overrides because BOPF's theme hides our item in two
    // different ways: mobile inherits visibility:hidden from a parent,
    // desktop sizes the flex child to 0x0. We force visibility, opacity,
    // and min-width on both the wrapper item and the link itself.
    style.textContent =
      '[data-bopf-pop-injected]{' +
        'visibility:visible !important;' +
        'opacity:1 !important;' +
        'min-width:max-content !important;' +
        'flex:0 0 auto !important;' +
      '}' +
      '.bopf-pop-link{' +
        'text-decoration:underline wavy ' + SCRIBBLE_COLOR + ' !important;' +
        'text-decoration-thickness:1.5px !important;' +
        'text-underline-offset:4px !important;' +
        'text-decoration-skip-ink:none !important;' +
        'visibility:visible !important;' +
        'opacity:1 !important;' +
        'min-width:max-content !important;' +
        'width:auto !important;' +
      '}';
    document.head.appendChild(style);
  }

  function stripActiveState(el) {
    // Theme-specific "this is the current page" classes that we don't
    // want to inherit on a freshly cloned item.
    var classes = [
      'active', 'current', 'is-active', 'is-current',
      'menu-item--active', 'header__active-menu-item',
      'list-menu__item--active'
    ];
    classes.forEach(function (c) { el.classList && el.classList.remove(c); });
    for (var i = 0; i < el.children.length; i++) stripActiveState(el.children[i]);
  }

  function buildItem(template) {
    // Clone an existing nav item so we inherit whatever <li>/<a> classes
    // the theme uses for spacing, font, hover state. Then strip submenus,
    // active state, and rewrite the link.
    var clone = template.cloneNode(true);
    clone.setAttribute(INJECTED_FLAG, '1');

    // Remove any nested submenus, dropdown panels, or mega-menu content
    // we picked up from the template item.
    var subs = clone.querySelectorAll(
      'ul, .menu-drawer, .header__submenu, [class*="submenu"], [class*="dropdown"], [class*="mega"]'
    );
    for (var i = 0; i < subs.length; i++) subs[i].remove();

    stripActiveState(clone);

    var link = clone.tagName === 'A' ? clone : clone.querySelector('a');
    if (!link) return null;

    link.href = POP_UP_HREF;
    link.removeAttribute('aria-current');
    link.classList.add('bopf-pop-link');
    link.textContent = POP_UP_LABEL;

    // Log the inherited classes so we can see what theme styling the
    // clone is carrying. Useful when a theme has CSS keyed to specific
    // classes that we'd need to keep or remove.
    console.log('[BOPF nav] cloned <' + link.tagName.toLowerCase() + '> classes:',
      link.className || '(none)');

    return clone;
  }

  // Class names that indicate an icon-only / utility link, not a real
  // text nav item. These links typically have width:24px and font-size:0
  // (or a visually-hidden text span), so cloning them as templates
  // produces a 0x0 invisible result no matter how hard we override CSS.
  var ICON_CLASS_PATTERNS = /(^|[\s_-])(icon|search|cart|account|toggle|burger|hamburger|close|menu-icon|drawer-toggle|nav-toggle)([\s_-]|$)/i;

  function isIconLink(el) {
    if (!el) return false;
    var cls = (el.className && typeof el.className === 'string') ? el.className : '';
    return ICON_CLASS_PATTERNS.test(cls);
  }

  // Find a "clean" item to use as the cloning template. We want an item
  // that has an <a> with simple text content and NO submenu/dropdown.
  // This avoids cloning the search/cart icon groups at the start/end of
  // the nav, and avoids cloning dropdown triggers (which would leave the
  // new item with an empty submenu chevron).
  function findCleanTemplate(menu) {
    var children = menu.children;
    // Pass 1: prefer items with link, simple text, no submenu, not icon.
    for (var i = 0; i < children.length; i++) {
      var c = children[i];
      var link = c.tagName === 'A' ? c : c.querySelector('a');
      if (!link) continue;
      // Skip icon/utility wrappers and links - they have width/font-size
      // constraints that collapse our text content to 0x0.
      if (isIconLink(c) || isIconLink(link)) continue;
      // First text node of the link, e.g. "Shop" not "Shop See More <img...>"
      var firstText = '';
      for (var k = 0; k < link.childNodes.length; k++) {
        if (link.childNodes[k].nodeType === 3) {
          firstText += link.childNodes[k].textContent;
        }
      }
      firstText = firstText.replace(/\s+/g, ' ').trim();
      if (!firstText || firstText.length > 40) continue;
      if (c.querySelector('ul, .submenu, [class*="dropdown"], [class*="sublist"]')) continue;
      return c;
    }
    // Pass 2: any child with a link, still skipping icon links.
    for (var j = 0; j < children.length; j++) {
      var c2 = children[j];
      var link2 = c2.tagName === 'A' ? c2 : c2.querySelector('a');
      if (!link2) continue;
      if (isIconLink(c2) || isIconLink(link2)) continue;
      return c2;
    }
    return null;
  }

  function findAnchorItem(items) {
    for (var i = 0; i < items.length; i++) {
      var link = items[i].tagName === 'A' ? items[i] : items[i].querySelector('a');
      if (!link) continue;
      // Use the link's first text node, ignoring any submenu content
      // that might be concatenated into textContent.
      var text = '';
      for (var k = 0; k < link.childNodes.length; k++) {
        if (link.childNodes[k].nodeType === 3) {
          text += link.childNodes[k].textContent;
        }
      }
      text = text.replace(/\s+/g, ' ').trim();
      if (!text) text = (link.textContent || '').replace(/\s+/g, ' ').trim();
      for (var j = 0; j < ANCHOR_PATTERNS.length; j++) {
        if (ANCHOR_PATTERNS[j].test(text)) {
          return { node: items[i], index: i };
        }
      }
    }
    return null;
  }

  function injectIntoMenu(menu, label) {
    if (!menu) return false;

    // Idempotency: skip if we've already injected here, or if a Pop Up
    // link already exists (manually added or from a previous run).
    if (menu.querySelector('[' + INJECTED_FLAG + ']')) return true;
    if (menu.querySelector('a[href*="/pages/the-bopf-popups-alquoz"]')) {
      console.log('[BOPF nav]', label, 'already has Pop Up link, skipping');
      return true;
    }

    var items = menu.children;
    if (items.length < 2) return false; // not a real nav menu

    // Anchor check FIRST. If we can't find a Shop/Catalog-like item to
    // insert after, this is probably not the main nav (could be a
    // utility bar like Log in / Create account / Search). Skip it
    // rather than inserting at the start, which would land Pop Up in
    // weird places like above Log in.
    var anchor = INSERT_AT_END ? null : findAnchorItem(items);
    if (!INSERT_AT_END && !anchor) {
      console.log('[BOPF nav]', label, 'has no anchor match, skipping');
      return false;
    }

    // Pick a clean item as cloning template (item with an <a>, simple
    // text, no submenu). Falls back to any child with a link.
    var template = findCleanTemplate(menu);
    if (!template) {
      console.warn('[BOPF nav] no clean template found in', label);
      return false;
    }

    var newItem = buildItem(template);
    if (!newItem) {
      console.warn('[BOPF nav] buildItem returned null for', label);
      return false;
    }

    if (INSERT_AT_END) {
      menu.appendChild(newItem);
      console.info('[BOPF nav] injected at end of', label);
    } else {
      anchor.node.insertAdjacentElement('afterend', newItem);
      console.info('[BOPF nav] injected after "' +
        (anchor.node.textContent || '').trim().slice(0, 24) + '" in', label);
    }

    // Visibility verification. If the new <a> is in the DOM but
    // getBoundingClientRect returns 0x0 or display:none is computed,
    // theme CSS is hiding it - log so we can debug instead of silently
    // failing. Run on next frame so layout has settled.
    var injectedLink = newItem.tagName === 'A' ? newItem : newItem.querySelector('a.bopf-pop-link');
    if (injectedLink) {
      requestAnimationFrame(function () {
        var rect = injectedLink.getBoundingClientRect();
        var cs = window.getComputedStyle(injectedLink);
        var visible = rect.width > 0 && rect.height > 0 && cs.display !== 'none' && cs.visibility !== 'hidden';
        if (visible) {
          console.info('[BOPF nav] verified visible in', label,
            '(' + Math.round(rect.width) + 'x' + Math.round(rect.height) + ')');
        } else {
          console.warn('[BOPF nav] injected but NOT visible in', label,
            '- rect:', rect.width + 'x' + rect.height,
            'display:', cs.display, 'visibility:', cs.visibility,
            '- the theme CSS is hiding it');
        }
      });
    }
    return true;
  }

  // Auto-discover nav menus by structure rather than CSS selectors. We
  // scan inside the header for any element with multiple direct children
  // where most children contain a link. This handles every theme: <ul>,
  // <nav> with direct <a>, custom elements, drawers, mega menus.
  function findNavMenus() {
    var headers = document.querySelectorAll('header, [role="banner"], .header, #header, .site-header, #shopify-section-header');
    if (!headers.length) return [];
    var found = [];
    var seen = new Set ? new Set() : null;
    function add(el) {
      if (!el) return;
      if (seen) {
        if (seen.has(el)) return;
        seen.add(el);
      } else if (found.indexOf(el) > -1) return;
      found.push(el);
    }
    for (var h = 0; h < headers.length; h++) {
      var header = headers[h];
      // Every UL or NAV inside the header is a candidate.
      var candidates = header.querySelectorAll('ul, nav, [role="list"], [role="navigation"]');
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        var kids = el.children;
        if (kids.length < 3) continue; // need 3+ items to be a real nav
        var withLinks = 0;
        for (var j = 0; j < kids.length; j++) {
          if (kids[j].tagName === 'A' || kids[j].querySelector('a')) withLinks++;
        }
        // Most children must have links. Filters out social icon rows
        // and utility lists with mixed content.
        if (withLinks < Math.ceil(kids.length * 0.6)) continue;
        add(el);
      }
    }
    return found;
  }

  function describeElement(el) {
    var tag = el.tagName.toLowerCase();
    var id = el.id ? '#' + el.id : '';
    var cls = '';
    if (el.className && typeof el.className === 'string') {
      var parts = el.className.trim().split(/\s+/).slice(0, 3);
      if (parts[0]) cls = '.' + parts.join('.');
    }
    return tag + id + cls;
  }

  function dumpDiagnostics() {
    console.group('[BOPF nav] DIAGNOSTIC: dumping header DOM structure');
    var headers = document.querySelectorAll('header, [role="banner"], .header, #header, .site-header, #shopify-section-header');
    console.log('Found ' + headers.length + ' header element(s)');
    if (!headers.length) {
      console.warn('No <header> or [role=banner] in the page. Tell me where your nav lives.');
      console.groupEnd();
      return;
    }
    for (var h = 0; h < headers.length; h++) {
      console.log('Header [' + h + ']:', describeElement(headers[h]));
      var lists = headers[h].querySelectorAll('ul, nav, [role="list"], [role="navigation"]');
      for (var i = 0; i < lists.length; i++) {
        var el = lists[i];
        var kids = el.children;
        var texts = [];
        for (var j = 0; j < kids.length && texts.length < 8; j++) {
          var t = (kids[j].textContent || '').replace(/\s+/g, ' ').trim().slice(0, 30);
          if (t) texts.push(t);
        }
        console.log(
          '  Candidate [' + i + ']:', describeElement(el),
          '| children: ' + kids.length,
          '| texts:', texts
        );
      }
    }
    console.log('--- Copy this whole block and paste it back to me ---');
    console.groupEnd();
  }

  function tryInject() {
    injectStyles();
    var menus = findNavMenus();
    if (!menus.length) return false;
    var injected = 0;
    for (var i = 0; i < menus.length; i++) {
      if (injectIntoMenu(menus[i], describeElement(menus[i]))) injected++;
    }
    return injected > 0;
  }

  function start() {
    console.log('[BOPF nav] start()');

    if (tryInject()) return;

    // Some themes hydrate the nav after DOMContentLoaded (mobile drawers,
    // mega menus). Retry every 100ms for up to 5 seconds.
    var attempts = 0;
    var maxAttempts = 50;
    var interval = setInterval(function () {
      attempts++;
      if (tryInject() || attempts >= maxAttempts) {
        clearInterval(interval);
        if (attempts >= maxAttempts) {
          console.warn('[BOPF nav] gave up after ' + attempts +
            ' attempts. Dumping DOM structure for debugging:');
          dumpDiagnostics();
        }
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();